class FreeGiftManager {
  constructor(config) {
    this._config = config;
    this._currentPopup = null;
    this._activeCampaignsCache = null;
    this._discardedCampaigns = new Set();
    this._isChecking = false;
    // Cooldown duration in minutes before re-evaluating an explicitly removed gift
    this._cooldownMinutes = 5; 
    this._init();
  }

  async _init() {
    this._patchFetch();
    await this._setupEventListeners();
  }

  /**
   * Patches window.fetch to monitor cart mutations and trigger gift rules checking.
   */
  _patchFetch() {
    const originalFetch = window.fetch;
    window.fetch = async (...args) => {
      const url = typeof args[0] === 'string' ? args[0] : args[0]?.url;

      if (url && (url.includes('/cart/add') || url.includes('/cart/change') || url.includes('/cart/update')) && !url.includes('_free_gift_internal')) {
        const response = await originalFetch(...args);
        
        // Re-evaluate cart state in microtask
        queueMicrotask(() => this.checkCart());
        return response;
      }

      return originalFetch(...args);
    };
  }

  /**
   * Stores a timestamped cooldown entry in localStorage for a specific campaign.
   */
  _setGiftCooldown(campaignTitle) {
    if (!campaignTitle) return;
    const cooldownData = {
      timestamp: Date.now(),
      cooldownUntil: Date.now() + (this._cooldownMinutes * 60 * 1000)
    };
    localStorage.setItem(`fg_cooldown_${campaignTitle}`, JSON.stringify(cooldownData));
  }

  /**
   * Clears all campaign cooldown entries from localStorage.
   */
  _clearAllCooldowns() {
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('fg_cooldown_')) {
        localStorage.removeItem(key);
      }
    });
  }

  /**
   * Checks whether a campaign is currently in its cooldown period.
   */
  _isGiftOnCooldown(campaignTitle) {
    const data = localStorage.getItem(`fg_cooldown_${campaignTitle}`);
    if (!data) return false;

    try {
      const { cooldownUntil } = JSON.parse(data);
      if (Date.now() < cooldownUntil) {
        return true; // Cooldown active; skip campaign
      } else {
        localStorage.removeItem(`fg_cooldown_${campaignTitle}`); // Expired; cleanup
        return false;
      }
    } catch {
      localStorage.removeItem(`fg_cooldown_${campaignTitle}`);
      return false;
    }
  }

  /**
   * Listens for DOM interactions including direct clicks on remove buttons.
   */
  async _setupEventListeners() {
    await documentReady();

    // Synchronously captures click events on cart removal buttons
    document.addEventListener('click', (e) => {
      const removeLink = e.target.closest('cart-remove-button a, .cart-item__remove');
      if (!removeLink) return;

      // Extract gift title from aria-label attribute (e.g., "Remove Free Gift - Lite Forest Brown")
      const ariaLabel = removeLink.getAttribute('aria-label') || '';
      
      // Iterate over active campaigns to check for a title match
      this._config.data.forEach(campaign => {
        const gifts = campaign.Product_to_Offers?.Product_to_Offer_FreeGift || [];
        
        const isMatch = gifts.some(g => ariaLabel.includes(g.freegiftitle)) || 
                        ariaLabel.includes(campaign.Title);

        if (isMatch) {
          // Set cooldown in localStorage BEFORE page reloads or redirects
          this._setGiftCooldown(campaign.Title);
        }
      });
    }, true); // Capture phase enabled for immediate execution

    document.querySelectorAll('[data-add-to-cart]').forEach(btn => 
      btn.addEventListener('click', () => this.checkCart())
    );
  }

  async checkCart() {
    if (this._isChecking) return;
    this._isChecking = true;
    try {
      const cart = await this._getCart();

      // Reset all cooldowns and dismissed states if the cart is completely empty
      if (!cart.item_count || cart.items.length === 0) {
        this._clearAllCooldowns();
        this._discardedCampaigns.clear();
        return;
      }

      await this._cleanExpiredGifts(cart);
      this._processCampaigns(cart);
    } catch (error) {
      console.error('Error processing gift cart logic:', error);
    } finally {
      this._isChecking = false;
    }
  }

  async _getCart() {
    const response = await fetch('/cart.js', { cache: 'no-store' });
    const cart = await response.json();
    await this._enrichCartItems(cart);
    return cart;
  }

  /**
   * Fetches missing product tags for items in the cart.
   */
  async _enrichCartItems(cart) {
    const itemsToFetch = cart.items.filter(item => !item.tags);
    if (itemsToFetch.length === 0) return;

    await Promise.allSettled(itemsToFetch.map(async item => {
      try {
        const productRes = await fetch(`/products/${item.handle}.json`, { cache: 'force-cache' });
        const productData = await productRes.json();
        item.tags = productData?.product?.tags ?? [];
      } catch {
        item.tags = [];
      }
    }));
  }

  /**
   * Removes free gifts from the cart if campaign conditions are no longer met.
   */
  async _cleanExpiredGifts(cart) {
    const giftIds = this._getAllGiftIds();
    const giftsToRemove = cart.items.filter(item => {
      const activeCampaign = this._config.data.find(campaign =>
        campaign.Product_to_Offers.Product_to_Offer_FreeGift.some(gift => gift.freegif === item.id)
      );
      return giftIds.some(g => g.freegif === item.id) &&
        !this._isGiftValid(item.id, cart) &&
        activeCampaign?.Title === item.properties?._free_gift;
    });

    if (giftsToRemove.length > 0) {
      await Promise.allSettled(giftsToRemove.map(item => this._removeFromCart(item.key)));
    }
  }

  _getAllGiftIds() {
    return this._config.data.flatMap(campaign =>
      campaign.Product_to_Offers.Product_to_Offer_FreeGift.map(gift => ({
        freegif: gift.freegif,
        campaignTitle: campaign.Title
      }))
    );
  }

  _isGiftValid(giftId, cart) {
    const campaign = this._config.data.find(c =>
      c.Product_to_Offers.Product_to_Offer_FreeGift.some(g => g.freegif === giftId)
    );
    return campaign ? this._isCampaignApplicable(cart, campaign) : false;
  }

  _processCampaigns(cart) {
    for (const campaign of this._getActiveCampaigns()) {
      if (this._discardedCampaigns.has(campaign.Title)) continue;

      // Skip current campaign if user recently removed its gift
      if (this._isGiftOnCooldown(campaign.Title)) continue;

      if (this._isCampaignApplicable(cart, campaign)) {
        this._handleCampaign(campaign, cart);
        break;
      }
    }
  }

  _getActiveCampaigns() {
    if (!this._activeCampaignsCache) {
      const now = Date.now();
      this._activeCampaignsCache = this._config.data
        .filter(c => {
          const start = new Date(c.Date_Start).getTime();
          const end = new Date(c.Date_End).getTime();
          return now >= start && now <= end;
        })
        .sort((a, b) => a.Priority - b.Priority);
    }
    return this._activeCampaignsCache;
  }

  _isCampaignApplicable(cart, campaign) {
    const rules = {
      'Quantity of products X in the cart': () => this._checkProducts(cart, campaign),
      'Quantity of Products X with Product Types Y in the cart': () => this._checkProductType(cart, campaign),
      'Quantity of Products X with Tags Y in the cart': () => this._checkTags(cart, campaign),
      'Cart subtotal': () => this._checkPriceRange(cart, campaign),
      'Cart Must Contain Specific Products with N Quantity': () => this._checkMultipleProductsWithQuantities(cart, campaign)
    };
    return rules[campaign.Select_Rules_Option]?.() ?? false;
  }

  _checkProducts(cart, campaign) {
    return cart.items.some(item =>
      campaign.Products.Product_Variant_Id.some(p => p.id === item.id && item.quantity >= p.quantity)
    );
  }

  _checkProductType(cart, campaign) {
    const total = cart.items
      .filter(i => i.product_type === campaign.ProductType && !i.properties?._free_gift)
      .reduce((sum, i) => sum + i.quantity, 0);
    return total >= campaign.Products.product_quantity;
  }

  _checkTags(cart, campaign) {
    const total = cart.items
      .filter(i => !i.properties?._free_gift && (Array.isArray(i.tags) ? i.tags : i.tags?.split(',').map(t => t.trim()))?.some(t => campaign.Tags.includes(t)))
      .reduce((sum, i) => sum + i.quantity, 0);
    return total >= campaign.Products.product_quantity;
  }

  _checkPriceRange(cart, campaign) {
    const total = cart.items
      .filter(i => !i.properties?._free_gift)
      .reduce((sum, i) => sum + (i.final_line_price || i.price * i.quantity), 0) / 100;
    return total >= Number(campaign.priceRange.min) && total <= Number(campaign.priceRange.max);
  }

  _checkMultipleProductsWithQuantities(cart, campaign) {
    return campaign.Products?.Product_Variant_Id?.every(p =>
      cart.items.some(i => i.id === p.id && i.quantity >= p.quantity)
    ) ?? false;
  }

  _handleCampaign(campaign, cart) {
    const gifts = campaign.Product_to_Offers.Product_to_Offer_FreeGift;
    if (!gifts || gifts.length === 0) {
      this._discardedCampaigns.add(campaign.Title);
      return;
    }

    const hasGift = cart.items.some(i => gifts.some(g => g.freegif === i.id));

    if (!hasGift) {
      campaign.Show_Popup_o_Automatic === 'Popup'
        ? this._showGiftPopup(campaign)
        : this._addGiftToCart(campaign);
    }
  }

  _showGiftPopup(campaign) {
    if (!this._currentPopup) {
      this._currentPopup = new GiftPopup(campaign, {
        onAdd: () => this._addGiftToCart(campaign),
        onClose: () => {
          this._setGiftCooldown(campaign.Title);
          this._discardedCampaigns.add(campaign.Title);
          this._currentPopup = null;
        }
      });
    }
  }

  async _addGiftToCart(campaign) {
    const gift = campaign.Product_to_Offers.Product_to_Offer_FreeGift[0];
    const cartContainer = document.querySelector("cart-notification") || document.querySelector("cart-drawer");

    if (!gift) {
      this._discardedCampaigns.add(campaign.Title);
      return;
    }

    try {
      const sections = window.location.pathname !== '/cart'
        ? cartContainer?.getSectionsToRender?.().map(s => s.id) || []
        : getSectionsToRender().map(s => s.section);

      const response = await fetch('/cart/add.js?_free_gift_internal=true', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{
            id: gift.freegif,
            quantity: campaign.Product_to_Offers.free_quantity,
            properties: { '_free_gift': campaign.Title }
          }],
          sections: sections
        })
      });

      if (!response.ok) throw new Error('Error adding gift to cart');

      const data = await response.json();
      if (window.location.pathname !== '/cart') {
        cartContainer?.renderContents?.(data);
      } else {
        window.location.reload();
      }
    } catch (error) {
      console.error('Error adding gift:', error);
      this._discardedCampaigns.add(campaign.Title);
    }
  }

  async _removeFromCart(key) {
    try {
      await fetch('/cart/change.js?_free_gift_internal=true', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: 0 })
      });
      window.location.reload();
    } catch (error) {
      console.error('Error removing gift:', error);
    }
  }
}

class GiftPopup {
  constructor(campaign, { onAdd, onClose }) {
    this._campaign = campaign;
    this._onAdd = onAdd;
    this._onClose = onClose;
    this._currentIndex = 0;
    this._popupElement = null;
    this._handleKeyPressBound = this._handleKeyPress.bind(this);
    this._init();
  }

  _init() {
    this._createPopup();
    this._setupCarousel();
    this._addEventListeners();
  }

  _createPopup() {
    this._popupElement = document.createElement('div');
    this._popupElement.className = 'free-gift-popup';
    this._popupElement.innerHTML = this._popupTemplate();
    document.body.appendChild(this._popupElement);

    requestAnimationFrame(() => {
      this._popupElement.classList.add('active');
      this._popupElement.querySelector('.popup-content')?.classList.add('active');
    });
  }

  _popupTemplate() {
    return `
      <div class="popup-content">
        <button class="close-popup" aria-label="Close">×</button>
        <h2 class="title_free_gift">${this._campaign.Modal_Title || ''}</h2>
        <p class="description_free_gift">${this._campaign.description_modal || ''}</p>
        <div class="carousel-container">
          ${this._campaign.Product_to_Offers.Product_to_Offer_FreeGift
            .map((gift, i) => this._giftTemplate(gift, i))
            .join('')}
          <div class="carousel-controls">
            <button class="carousel-button carousel-prev" aria-label="Previous">❮</button>
            <button class="carousel-button carousel-next" aria-label="Next">❯</button>
          </div>
        </div>
      </div>
    `;
  }

  _giftTemplate(gift, index) {
    return `
      <div class="carousel-item ${index === 0 ? 'active' : ''}" data-index="${index}">
        <form method="post" class="gift-form">
          <input type="hidden" name="id" value="${gift.freegif}">
          <img src="${gift.freegifimage}" alt="${gift.freegiftitle}" loading="lazy">
          <h3>${gift.freegiftitle}</h3>
          <button type="submit" class="gift-option">
            ADD TO CART
            <div class="loading-overlay">
              <svg class="spinner" viewBox="0 0 66 66"><circle class="path" fill="none" cx="33" cy="33" r="30"></circle></svg>
            </div>
          </button>
        </form>
      </div>
    `;
  }

  _setupCarousel() {
    this._items = this._popupElement.querySelectorAll('.carousel-item');
    this._totalItems = this._items.length;
    this._updateButtons();
  }

  _addEventListeners() {
    this._popupElement.querySelector('.carousel-prev').addEventListener('click', () => this._prev());
    this._popupElement.querySelector('.carousel-next').addEventListener('click', () => this._next());
    this._popupElement.querySelector('.close-popup').addEventListener('click', () => this._close());

    this._popupElement.querySelectorAll('.gift-form').forEach(form =>
      form.addEventListener('submit', e => this._handleSubmit(e))
    );

    document.addEventListener('keydown', this._handleKeyPressBound);
  }

  async _handleSubmit(e) {
    e.preventDefault();
    const form = e.target;
    const button = form.querySelector('.gift-option');
    const loader = form.querySelector('.loading-overlay');

    try {
      button.disabled = true;
      loader?.classList.add('visible');
      await this._onAdd();
      this._close();
    } catch (error) {
      console.error('Error in popup submit:', error);
      this._close();
    } finally {
      button.disabled = false;
      loader?.classList.remove('visible');
    }
  }

  _handleKeyPress(event) {
    if (event.key === 'Escape') this._close();
    if (event.key === 'ArrowLeft') this._prev();
    if (event.key === 'ArrowRight') this._next();
  }

  _prev() {
    if (this._currentIndex > 0) {
      this._currentIndex--;
      this._updateCarousel();
    }
  }

  _next() {
    if (this._currentIndex < this._totalItems - 1) {
      this._currentIndex++;
      this._updateCarousel();
    }
  }

  _updateCarousel() {
    this._items.forEach((item, i) => item.classList.toggle('active', i === this._currentIndex));
    this._updateButtons();
  }

  _updateButtons() {
    const prev = this._popupElement.querySelector('.carousel-prev');
    const next = this._popupElement.querySelector('.carousel-next');
    if (prev) prev.style.visibility = this._currentIndex === 0 ? 'hidden' : 'visible';
    if (next) next.style.visibility = this._currentIndex === this._totalItems - 1 ? 'hidden' : 'visible';
  }

  _close() {
    document.removeEventListener('keydown', this._handleKeyPressBound);
    this._popupElement.classList.remove('active');

    setTimeout(() => {
      this._popupElement.remove();
      this._onClose();
    }, 300);
  }
}

const documentReady = () => new Promise(resolve => {
  if (document.readyState === 'complete' || document.readyState === 'interactive') {
    resolve();
  } else {
    document.addEventListener('DOMContentLoaded', resolve, { once: true });
  }
});

function getSectionsToRender() {
  return [
    { id: 'main-cart-items', section: document.getElementById('main-cart-items')?.dataset.id, selector: '.js-contents' },
    { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
    { id: 'main-cart-footer', section: document.getElementById('main-cart-footer')?.dataset.id, selector: '.js-contents' }
  ].filter(section => section.section);
}

const initializeGiftManager = async () => {
  try {
    const response = await fetch('/pages/searchgift', { credentials: 'same-origin' });
    const data = await response.json();
    const manager = new FreeGiftManager(data);
    if (window.location.pathname === '/cart') {
      await manager.checkCart();
    }
    return manager;
  } catch (error) {
    console.error('Gift Manager initialization failed:', error);
  }
};

initializeGiftManager();